import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { PATH_PERSON_DEFAULT } from '../../../app/constants/paths.constant';
import type { ReferencesContext_Civility } from '../../../contexts/ReferencesContext';
import type { ReferencesContext_GenericStatus } from '../../../contexts/ReferencesContext';

import { UseCase } from '../enums/use-case.enum';
import type { ThirdPartiesCmp_Person } from '../fragments/person.fragment';

import { THIRD_PARTY_STATUS_ID } from './get-third-party-columns';

interface ActionProps {
  readonly id: Id;
}

const Action = ({ id }: Readonly<ActionProps>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_PERSON_DEFAULT, { id });
    void navigate(path);
  }, [navigate, id]);

  const actionLabel = 'Consulter la Personne';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getPersonColumns = (
  useCase: UseCase,
  civilities: readonly ReferencesContext_Civility[],
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<ThirdPartiesCmp_Person>[] => {
  const columns: readonly GridColDef<ThirdPartiesCmp_Person>[] = [
    ...(useCase === UseCase.Visualize
      ? ([
          {
            field: 'actions',
            headerName: 'Actions',
            description: 'Actions',
            editable: false,
            sortable: false,
            type: 'actions',
            getActions: (
              params: Readonly<GridRowParams<ThirdPartiesCmp_Person>>,
            ) => [<Action id={params.row._id} key="consult" />],
          },
        ] as const)
      : EMPTY_ARRAY),
    {
      field: nameof<ThirdPartiesCmp_Person>('civilityId'),
      headerName: 'Civilité',
      width: 200,
      description: 'Civilité',
      editable: false,
      sortable: true,
      type: 'singleSelect',
      valueGetter: (_: unknown, row: Readonly<ThirdPartiesCmp_Person>) =>
        civilities.find((civility) => civility._id === row.civilityId)?.code ??
        'Inconnu',
      valueOptions: civilities.map((civility) => ({
        value: civility.code,
        label: civility.label,
      })),
    },
    {
      field: nameof<ThirdPartiesCmp_Person>('firstName'),
      headerName: 'Prénom',
      width: 350,
      description: 'Prénom',
      editable: false,
      sortable: true,
      type: 'string',
    },
    {
      field: nameof<ThirdPartiesCmp_Person>('lastName'),
      headerName: 'Nom',
      width: 350,
      description: 'Nom',
      editable: false,
      sortable: true,
      type: 'string',
    },
    {
      field: THIRD_PARTY_STATUS_ID,
      headerName: 'Statut',
      width: 200,
      description: 'Statut',
      editable: false,
      sortable: true,
      hideable: true,
      type: 'singleSelect',
      valueGetter: (_: unknown, row: Readonly<ThirdPartiesCmp_Person>) =>
        genericStatuses.find(
          (genericStatus) => genericStatus._id === row.statusId,
        )?.code ?? 'Inconnu',
      valueOptions: genericStatuses.map((genericStatus) => ({
        value: genericStatus.code,
        label: genericStatus.label,
      })),
    },
  ] as const;

  return columns;
};
